import React from "react";
import { Box, Typography } from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
} from "recharts";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useTheme, useMediaQuery } from "@mui/material";

const FTEGapAnalysisChart = ({ title, chartData, data, setSelectedJob, scenario }) => {
  const { t } = useTranslation();
  const colors = [
    "rgba(54, 162, 235, 0.6)", // ✅ Required (blue)
    "rgba(75, 192, 192, 0.6)", // ✅ Actual (teal/greenish)
    "rgba(255, 206, 86, 0.6)", // 🟡 Difference (base color, overridden dynamically)
  ];
  const barKeys = ["required", "actual", "difference"];

  // Prepare data rows for the chart
  const chartRows =
    chartData?.labels?.map((label, i) => ({
      name: label,
      required: chartData.datasets[0].data[i],
      actual: chartData.datasets[1].data[i],
      difference: chartData.datasets[2].data[i],
    })) || [];

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm")); // xs/small screens

  return (
    <Box sx={{ mb: 4 }}>
      {/* Title */}
      <Typography variant="body2" sx={{ mb: 1, mt: 1 }}>
        {title}
      </Typography>

      {/* Chart container */}
      <Box
        sx={{
          height: { xs: 350, md: 400 },
          overflowX: "auto",
          whiteSpace: "nowrap",
          pb: scenario?.type === "GAHAR Phase 1" ? 2 : 8,
        }}
      >
        <Box sx={{ minWidth: `${chartData.labels.length * 100}px`, height: "100%" }}>
          {chartData?.labels?.length > 0 && (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartRows}
                onClick={(d) => {
                  if (d) {
                    const job = data.find((j) => j.jobName === d.activeLabel);
                    if (job) {
                      setSelectedJob(job.id ? job.id : job.jobId);
                    }
                  }
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  {...(scenario?.type === "GAHAR Phase 1" && !isSmallScreen
                    ? { angle: 0, textAnchor: "middle", tick: { fontSize: 14, dy: 20 } }
                    : { angle: -45, textAnchor: "end" })}
                  interval={0}
                  height={60}
                />

                <YAxis />
                <Tooltip />

                {barKeys.map((key, i) => {
                  if (key === "difference") {
                    return (
                      <Bar
                        key={key}
                        dataKey={key}
                        name={t(`user.manpowerPlanningAnalysis.chart.${key}FTE`)}
                        label={{
                          position: "top",
                          formatter: (value) => `${value.toLocaleString()} `,
                        }}
                        cursor="pointer"
                      >
                        {chartRows.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.difference >= 0 ? "#ef5350" : "#ffa726"}
                          />
                        ))}
                      </Bar>
                    );
                  } else {
                    return (
                      <Bar
                        key={key}
                        dataKey={key}
                        fill={colors[i]}
                        name={t(`user.manpowerPlanningAnalysis.chart.${key}FTE`)}
                        label={{
                          position: "top",
                          formatter: (value) => `${value.toLocaleString()}`,
                        }}
                        cursor="pointer"
                      />
                    );
                  }
                })}
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Box>

      {/* Legend */}

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexWrap: "wrap", // ✅ allows wrapping on small screens
          gap: { xs: 1.5, sm: 2, md: 3 }, // ✅ responsive spacing
          mt: 1,
          textAlign: "center", // ✅ better alignment when wrapping
        }}
      >
        {barKeys.map((key, i) => (
          <Box
            key={key}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flex: { xs: "1 1 100%", sm: "auto" }, // ✅ stack vertically on very small screens
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: colors[i],
                borderRadius: "2px",
              }}
            />
            <Typography variant="body2">
              {t(`user.manpowerPlanningAnalysis.chart.${key}FTE`)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

FTEGapAnalysisChart.propTypes = {
  title: PropTypes.string.isRequired,
  chartData: PropTypes.shape({
    labels: PropTypes.arrayOf(PropTypes.string).isRequired,
    datasets: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        data: PropTypes.arrayOf(PropTypes.number).isRequired,
        backgroundColor: PropTypes.string.isRequired,
        borderColor: PropTypes.string,
        borderWidth: PropTypes.number,
        barPercentage: PropTypes.number,
      })
    ).isRequired,
  }).isRequired,
  data: PropTypes.any.isRequired,
  setSelectedJob: PropTypes.func.isRequired,
  scenario: PropTypes.object,
};

export default FTEGapAnalysisChart;
